import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import Avatar from "@/components/base/Avatar";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import { useToast } from "@/components/feature/ToastProvider";
import { useAuth } from "@/components/feature/AuthProvider";
import type { TugDetail, TugTeam } from "@/lib/types";
import { closeTug, deleteTug, playTug } from "../api";
import {
  STATE_BADGE,
  STATE_BORDER,
  STATE_STRIP,
  TEAM_BG,
  TEAM_SOFT,
  TEAM_TEXT,
  formatCountdown,
  formatDeadline,
  isDim,
  modeLabel,
  stateLabel,
  teamKey,
  teamName,
} from "../utils";
import TugPlay from "../components/TugPlay";
import TugGauge from "../components/TugGauge";
import TugResultList from "../components/TugResultList";

// /tug/:no — 참가 + 결과
export default function TugDetailPage() {
  const { no } = useParams();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { member, requireLogin } = useAuth();
  const now = useNow();

  const { data, loading, error, reload, serverTs } = useApi<TugDetail>(
    no ? `tug.php?no=${no}` : null
  );

  const [item, setItem] = useState<TugDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [playTeam, setPlayTeam] = useState<TugTeam | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [shakeTeam, setShakeTeam] = useState<TugTeam | null>(null);
  const reloadedRef = useRef(false);
  const autoPlayedRef = useRef(false);

  useEffect(() => {
    if (data) {
      setItem(data);
      setConfirmDel(false);
      reloadedRef.current = false;
    }
  }, [data]);

  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;
  const remaining = Math.max(0, (item?.end_in ?? 0) - elapsed);

  // 마감 시간이 되면 목록을 다시 불러온다 (서버가 그때 자동 종료 처리).
  useEffect(() => {
    if (item?.state === "open" && remaining === 0 && !reloadedRef.current) {
      reloadedRef.current = true;
      reload();
    }
  }, [item?.state, remaining, reload]);

  const startPlay = useCallback(
    (t: TugTeam) => {
      if (!member) {
        requireLogin(`/tug/${no ?? ""}`);
        return;
      }
      setPlayTeam(t);
    },
    [member, requireLogin, no]
  );

  // 등록 직후(?play=1)면 바로 두드리기 화면을 띄운다. (등록자도 참가해야 한다)
  useEffect(() => {
    if (!item || autoPlayedRef.current) return;
    if (
      searchParams.get("play") === "1" &&
      item.state === "open" &&
      item.is_joined !== 1
    ) {
      autoPlayedRef.current = true;
      const fromQuery = searchParams.get("team");
      setPlayTeam(fromQuery ? teamKey(fromQuery) : teamKey(item.host_team));
    }
  }, [item, searchParams]);

  const finishPlay = useCallback(
    async (taps: number) => {
      if (!no || !playTeam) return;
      setBusy(true);
      try {
        const res = await playTug(Number(no), playTeam, taps);
        const d = res.data ?? {};
        toast(res.msg || "기록을 제출했습니다.", "success");
        setPlayTeam(null);
        // 내가 마지막 자리를 채웠으면 결과가 바로 공개된다.
        if (d.closed === 1) {
          window.setTimeout(reload, 500);
        } else {
          reload();
        }
      } catch (e) {
        const err = e as Error & { status?: number };
        toast(err.message || "참여에 실패했습니다.", "error");
        // 인원차 초과는 서버가 409 로 거절한다 → 반대 팀 버튼을 흔들어 강조
        if (err.status === 409 && playTeam) {
          const t = playTeam;
          setShakeTeam(t);
          window.setTimeout(() => setShakeTeam(null), 700);
        }
        setPlayTeam(null);
        reload();
      } finally {
        setBusy(false);
      }
    },
    [no, playTeam, toast, reload]
  );

  const doClose = useCallback(async () => {
    if (!no) return;
    setBusy(true);
    try {
      const res = await closeTug(Number(no));
      toast(res.msg || "경기를 종료했습니다. 결과가 공개됩니다.", "success");
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "종료에 실패했습니다.", "error");
    } finally {
      setBusy(false);
    }
  }, [no, toast, reload]);

  const doDelete = useCallback(async () => {
    if (!no) return;
    setBusy(true);
    try {
      const res = await deleteTug(Number(no));
      toast(res.msg || "삭제했습니다.", "success");
      if (typeof window.REACT_APP_NAVIGATE === "function") {
        window.REACT_APP_NAVIGATE("/tug");
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : "삭제에 실패했습니다.", "error");
      setConfirmDel(false);
      setBusy(false);
    }
  }, [no, toast]);

  if (loading) {
    return (
      <>
        <PageBanner
          title="단체 줄다리기"
          breadcrumb={[{ label: "줄다리기", to: "/tug" }, { label: "상세" }]}
        />
        <section className="mx-auto max-w-3xl space-y-4 px-4 py-10 md:px-6">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-56 w-full rounded-xl" />
        </section>
      </>
    );
  }

  if (error || !item) {
    return (
      <>
        <PageBanner
          title="단체 줄다리기"
          breadcrumb={[{ label: "줄다리기", to: "/tug" }, { label: "상세" }]}
        />
        <section className="mx-auto max-w-3xl px-4 py-12 md:px-6">
          <EmptyState
            icon="ri-error-warning-line"
            title="경기를 불러오지 못했습니다"
            description={error ?? "존재하지 않는 경기입니다."}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        </section>
      </>
    );
  }

  const cd = formatCountdown(remaining);
  const unlimited = item.limit_cnt === 0;
  const aName = teamName(item.team_a, "A");
  const bName = teamName(item.team_b, "B");
  const mine = item.is_mine === 1;
  const joined = item.is_joined === 1;
  const open = item.state === "open";
  const dim = isDim(item.state);
  const myTeam = item.my ? teamKey(item.my.team) : null;
  const aFull = !unlimited && (item.a_left ?? 0) <= 0;
  const bFull = !unlimited && (item.b_left ?? 0) <= 0;
  const allFull = aFull && bFull;

  // 인원 균형(balance). 서버가 값을 줄 때만 적용한다. 한쪽에 몰리는 것을 미리 막는다.
  const balance = typeof item.balance === "number" ? item.balance : 0;
  const aBlocked = balance > 0 && (item.a_cnt ?? 0) - (item.b_cnt ?? 0) >= balance;
  const bBlocked = balance > 0 && (item.b_cnt ?? 0) - (item.a_cnt ?? 0) >= balance;

  const btnBase =
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-bold transition-colors disabled:opacity-50";

  const teamCard = (t: TugTeam) => {
    const name = t === "A" ? aName : bName;
    const other = t === "A" ? bName : aName;
    const cnt = (t === "A" ? item.a_cnt : item.b_cnt) ?? 0;
    const full = t === "A" ? aFull : bFull;
    const blocked = !full && (t === "A" ? aBlocked : bBlocked);
    return (
      <div
        className={`flex flex-col items-center rounded-xl border p-4 text-center ${TEAM_SOFT[t]} ${
          full ? "opacity-60" : ""
        }`}
      >
        <p className={`font-heading text-lg font-bold ${TEAM_TEXT[t]}`}>{name}</p>
        <p className="mt-1 text-sm text-foreground-600">
          <strong className="text-foreground-900">{cnt}</strong>
          {unlimited ? "명 · 제한 없음" : ` / ${item.limit_cnt}명`}
        </p>
        {blocked && (
          <p className="mt-2 text-xs font-semibold text-secondary-900">
            인원 균형을 위해 {other}으로 참가해 주세요
          </p>
        )}
        <button
          type="button"
          onClick={() => startPlay(t)}
          disabled={busy || full || blocked}
          className={`mt-3 w-full whitespace-nowrap rounded-md py-3 text-sm font-bold disabled:opacity-60 ${TEAM_BG[t]} ${
            shakeTeam === t ? "tug-shake" : ""
          }`}
        >
          {full ? "정원 마감" : blocked ? "참가 불가" : `${name}으로 참가`}
        </button>
      </div>
    );
  };

  return (
    <>
      <PageBanner
        title="단체 줄다리기"
        breadcrumb={[{ label: "줄다리기", to: "/tug" }, { label: "상세" }]}
      />

      <section className="mx-auto max-w-3xl px-4 py-8 md:px-6 md:py-12">
        <article
          className={`relative overflow-hidden rounded-xl border bg-background-50 ${
            STATE_BORDER[item.state]
          } ${dim ? "opacity-90" : ""}`}
        >
          <span
            className={`absolute inset-y-0 left-0 w-1.5 ${STATE_STRIP[item.state]}`}
            aria-hidden="true"
          ></span>

          <div className="p-6 pl-7 md:p-8 md:pl-9">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                  STATE_BADGE[item.state]
                }`}
              >
                {stateLabel(item.state, item.state_txt)}
              </span>
              {mine && (
                <span className="inline-block rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-900">
                  내가 연 경기
                </span>
              )}
              {joined && (
                <span className="inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
                  참가완료
                </span>
              )}
            </div>

            <h2 className="mt-4 flex items-start gap-2 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
              <span aria-hidden="true">🪢</span>
              {item.subject}
            </h2>

            {/* 등록자 */}
            <div className="mt-4 flex items-center gap-3">
              <Avatar name={item.host?.name} photo={item.host?.photo} size={44} />
              <span className="text-sm text-foreground-600">
                <strong className="font-semibold text-foreground-900">
                  {item.host?.aho} {item.host?.name}
                </strong>{" "}
                님이 열었습니다
              </span>
            </div>

            {/* 보상 */}
            {item.has_reward === 1 && item.reward ? (
              <p className="mt-4 inline-flex items-center gap-2 rounded-lg bg-accent-50 px-4 py-2.5 text-sm font-semibold text-accent-900">
                <span aria-hidden="true">🎁</span>
                이긴 팀 : {item.reward}
              </p>
            ) : null}

            {/* 핵심 영역 */}
            <div className="mt-5">
              {open ? (
                <div className="rounded-2xl border border-background-200 bg-background-100 px-5 py-6">
                  {/* 마감 · 인원 */}
                  <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-1.5 text-sm text-foreground-600">
                      <i
                        className="ri-alarm-warning-line text-foreground-400"
                        aria-hidden="true"
                      ></i>
                      마감까지{" "}
                      <strong
                        className={`tabular-nums ${
                          cd.urgent ? "animate-pulse text-accent-700" : "text-foreground-950"
                        }`}
                      >
                        {cd.text}
                      </strong>
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-sm text-foreground-500">
                      <i className="ri-lock-2-line text-foreground-400" aria-hidden="true"></i>
                      점수는 종료 후 공개
                    </span>
                  </div>

                  {joined ? (
                    <div className="text-center">
                      <p className="text-sm text-foreground-600">참가한 팀</p>
                      <p
                        className={`mt-1 font-heading text-2xl font-extrabold ${
                          TEAM_TEXT[myTeam ?? "A"]
                        }`}
                      >
                        {myTeam === "A" ? aName : bName}
                      </p>
                      <p className="mt-3 text-sm text-foreground-600">
                        기록을 제출했습니다. 결과는 종료 후 공개됩니다.
                      </p>
                      {typeof item.my?.taps === "number" && item.my.taps >= 0 && (
                        <p className="mt-2 text-sm text-foreground-700">
                          내 기록{" "}
                          <strong className="font-heading text-lg tabular-nums text-foreground-950">
                            {item.my.taps}
                          </strong>
                          번
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="mb-3 text-center text-sm font-semibold text-foreground-700">
                        참가할 팀을 고르세요
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        {teamCard("A")}
                        {teamCard("B")}
                      </div>
                      {allFull && (
                        <p className="mt-3 text-center text-sm font-semibold text-accent-700">
                          양 팀 정원이 모두 찼습니다. 곧 종료됩니다.
                        </p>
                      )}
                      <p className="mt-3 text-center text-xs text-foreground-500">
                        팀을 고르면 {item.tap_sec}초 동안 화면을 두드립니다. 한 번 제출하면
                        바꿀 수 없습니다.
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <TugGauge
                  aName={aName}
                  bName={bName}
                  aScore={item.a_score ?? item.a_taps ?? 0}
                  bScore={item.b_score ?? item.b_taps ?? 0}
                  aTaps={item.a_taps ?? 0}
                  bTaps={item.b_taps ?? 0}
                  aCnt={item.a_cnt ?? 0}
                  bCnt={item.b_cnt ?? 0}
                  compareN={item.compare_n ?? 0}
                  scoreBasis={item.score_basis}
                  judgeMode={item.judge_mode}
                  modeLabelText={item.mode_label}
                  winTeam={item.win_team}
                  winName={item.win_name}
                />
              )}
            </div>

            {/* 등록자 액션 */}
            {mine && (
              <div className="mt-6 flex flex-wrap items-center gap-2">
                {open && (
                  <button
                    type="button"
                    onClick={doClose}
                    disabled={busy}
                    className={`${btnBase} bg-primary-500 text-background-50 hover:bg-primary-600`}
                  >
                    <i className="ri-stop-circle-line" aria-hidden="true"></i>
                    지금 종료하고 결과 공개
                  </button>
                )}
                {confirmDel ? (
                  <>
                    <button
                      type="button"
                      onClick={doDelete}
                      disabled={busy}
                      className={`${btnBase} bg-accent-500 text-foreground-950 hover:bg-accent-600`}
                    >
                      삭제 확인
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDel(false)}
                      disabled={busy}
                      className={`${btnBase} border border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100`}
                    >
                      취소
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDel(true)}
                    disabled={busy}
                    className={`${btnBase} border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100`}
                  >
                    <i className="ri-delete-bin-line" aria-hidden="true"></i>
                    삭제
                  </button>
                )}
                {open && !joined && (
                  <span className="flex items-center px-1 text-xs text-foreground-400">
                    등록자는 «내 팀»으로 참가합니다.
                  </span>
                )}
              </div>
            )}

            {/* 마감 정보 */}
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-background-200 pt-4 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-timer-line" aria-hidden="true"></i>
                두드리는 시간 {item.tap_sec}초
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-scales-3-line" aria-hidden="true"></i>
                승부 기준 {modeLabel(item.judge_mode, item.mode_label)}
              </span>
              {item.deadline ? (
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  {formatDeadline(item.deadline)} 마감
                </span>
              ) : null}
            </div>
          </div>
        </article>

        {/* 결과 목록 (종료 후) */}
        {!open && (
          <div className="mt-8">
            <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-list-ordered text-foreground-500" aria-hidden="true"></i>
              팀별 결과
              <span className="rounded-full bg-background-100 px-2.5 py-0.5 text-sm font-semibold text-foreground-600">
                {item.joins?.length ?? 0}명
              </span>
            </h3>
            <TugResultList
              joins={item.joins ?? []}
              aName={aName}
              bName={bName}
              compareN={item.compare_n ?? 0}
            />
          </div>
        )}

        {/* 목록으로 */}
        <div className="mt-8 flex justify-center">
          <Link
            to="/tug"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            목록으로
          </Link>
        </div>
      </section>

      {/* 참가 플로우 (3-2-1 → 두드리기 → 자동 제출) */}
      {playTeam && (
        <TugPlay
          team={playTeam}
          name={playTeam === "A" ? aName : bName}
          tapSec={item.tap_sec || 20}
          submitting={busy}
          onFinish={finishPlay}
        />
      )}
    </>
  );
}